<template>
  <v-progress-linear
    v-if="loading"
    indeterminate
    color="secondary"
    class="mb-2"
  />
  <v-container
    fluid
    class="pa-0"
  >
    <v-row
      v-if="deposits && deposits.length"
      class="ma-0 ga-0"
    >
      <v-col
        cols="12"
        class="pa-0"
      >
        <h3>Open deposits</h3>
        <DepositTable
          v-if="(openDeposits.length > 0)"
          class="w-100"
          :deposits="openDeposits"
          :headers="true"
          :admin-user-email="adminUserEmail"
          :admin-user-name="adminUserName"
          :admin-search="adminSearch"
          :admin-page="adminPage"
          :empty-deposit-ids="emptyDepositIds"
          @refresh="getData"
        />
        <p v-else>
          No open deposits.
        </p>
        <div v-if="(closedDeposits.length > 0)">
          <br>
          <h3>Closed deposits</h3>
          <p>These deposits have been closed and submitted for ingest, so no new files can be added to them</p>
          <DepositTable
            v-if="(closedDeposits.length > 0)"
            class="w-100"
            :deposits="closedDeposits"
            :headers="(openDeposits.length == 0 || false)"
            :admin-user-email="adminUserEmail"
            :admin-user-name="adminUserName"
            :admin-search="adminSearch"
            :admin-page="adminPage"
            @refresh="getData"
          />
        </div>
      </v-col>
    </v-row>
    <v-row v-else>
      <div v-if="hasIdentityId">
        <br>
        <p style="margin-left: 1em">
          No deposits found.
        </p>
        <br>
        <br>
      </div>
      <div v-else>
        <br>
        <p style="margin-left: 1em; color: #c62828; display: flex; align-items: center; gap: 0.4em;">
          <v-icon
            color="error"
            size="20"
          >
            mdi-alert
          </v-icon>
          This user needs to sign in before you can create a deposit for them.
        </p>
        <br>
        <br>
      </div>
    </v-row>
  </v-container>
</template>

<script>
import config from "@/config";
import { Auth } from '@/utils/amplify-compat';
import { S3Client, ListObjectsV2Command, GetObjectCommand } from "@aws-sdk/client-s3";
import _ from "lodash";
import DepositTable from '@/components/DepositTable.vue'
import { on, off } from '@/utils/bus'
import { depositHasFiles } from '@/utils/depositFiles'

export default {
  name: "AdminDepositList",
  components: { DepositTable },
  props: {
    dialog: { type: Boolean, default: false },
    depositor: { type: Object, default: () => ({}) },
    adminUserEmail: { type: String, default: '' },
    adminUserName: { type: String, default: '' },
    adminSearch: { type: String, default: '' },
    adminPage: { type: Number, default: 1 },
  },
  emits: ['refresh'],
  data() {
    return {
      deposits: [],
      emptyDepositIds: [],
      identityId: null,
      loading: false,
    }
  },
  computed: {
    thisdepositor() {
      return this.depositor && Object.keys(this.depositor).length
        ? this.depositor
        : (this.$cognitoAuth?.getCurrentUser?.() ?? {});
    },
    hasIdentityId() {
      return Boolean(this.thisdepositor?.['custom:identityid'] || this.identityId);
    },
    openDeposits() {
      return _.orderBy(this.deposits.filter(d => d.status === "open"), ["createdDate"], ["desc"]);
    },
    closedDeposits() {
      return _.orderBy(this.deposits.filter(d => d.status !== "open"), ["createdDate"], ["desc"]);
    },
  },
  mounted() {
    on('refresh', this.getData)
  },
  beforeUnmount() {
    off('refresh', this.getData)
  },
  async created() {
    await this.getData();
  },
  methods: {
    async streamToString(body) {
      // Browser: GetObject Body is usually a ReadableStream
      if (body && typeof body.getReader === 'function') {
        const reader = body.getReader();
        const decoder = new TextDecoder("utf-8");
        let out = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          out += decoder.decode(value, { stream: true });
        }
        out += decoder.decode(); // flush
        return out;
      }
      // Blob fallback
      if (body && typeof body.text === 'function') return body.text();
      // Node/buffer-like fallback
      if (body && body.transformToByteArray) {
        const bytes = await body.transformToByteArray();
        return new TextDecoder("utf-8").decode(bytes);
      }
      return "";
    },

    // Only open deposits with no uploaded files are eligible for deletion
    async findEmptyOpenDeposits(client) {
      const results = await Promise.all(this.openDeposits.map(async (d) => {
        try {
          return (await depositHasFiles(client, this.identityId, d.id)) ? null : d.id;
        } catch (e) {
          console.warn('file check failed for', d.id, e);
          return null;
        }
      }));
      return results.filter(Boolean);
    },

    async getData() {
      this.loading = true;
      this.emptyDepositIds = [];
      try {
        const creds = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(creds),
          forcePathStyle: true,
        });

        this.identityId = this.thisdepositor?.['custom:identityid'] || null;

        if (!this.identityId) {
          console.warn('Selected user has no custom:identityid; cannot list deposits.');
          this.deposits = [];
          return; // finally will still run
        }

        const prefix = `private/${this.identityId}/deposits/`;
        console.log('LIST prefix:', prefix);

        // If you might have many deposits, paginate; otherwise this single call is fine.
        const listed = await client.send(new ListObjectsV2Command({
          Bucket: config.bucketName,
          Prefix: prefix,
        }));

        const keys = (listed.Contents || []).map(o => o.Key).filter(Boolean);
        console.log('LIST count:', keys.length);

        if (!keys.length) {
          this.deposits = [];
          return; // finally will still run
        }

        const objects = await Promise.all(keys.map(async (Key) => {
          try {
            const out = await client.send(new GetObjectCommand({ Bucket: config.bucketName, Key }));
            const text = await this.streamToString(out.Body);
            if (!text) return null;
            const obj = JSON.parse(text);
            obj.key = Key;
            return obj;
          } catch (e) {
            console.warn('getObject failed for', Key, e);
            return null;
          }
        }));

        this.deposits = objects.filter(Boolean);
        console.log('DEPOSITS parsed:', this.deposits.length);

        this.emptyDepositIds = await this.findEmptyOpenDeposits(client);
      } catch (err) {
        console.error('getData error', err);
        this.deposits = [];
        this.emptyDepositIds = [];
      } finally {
        this.loading = false; // always turn the spinner off
      }
    },
  }
}
</script>
